/**
 * EntityPicker — a Combobox that lets the user:
 *  - Select an existing merchant or person.
 *  - Create a new merchant or person by typing a name with no exact match.
 *
 * The predicted entity type (from classifyEntity) comes first in the create list.
 */

import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@nimit9/signet-ui/components/combobox"
import { Label } from "@nimit9/signet-ui/components/label"
import { classifyEntity } from "@paisa/parsers"
import { useState } from "react"

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------

interface EntityPickerMerchant {
  id: string
  cleanName: string
}

interface EntityPickerPerson {
  id: string
  name: string
}

/** What onChange emits. */
export type EntityPickerSelection =
  | { kind: "existing"; entity: { type: "merchant" | "person"; id: string } }
  | { kind: "create"; type: "merchant" | "person"; name: string }
  | { kind: "clear" }

export interface EntityPickerProps {
  merchants: EntityPickerMerchant[]
  persons: EntityPickerPerson[]
  /** Currently linked entity, or null if none. */
  value: { type: "merchant" | "person"; id: string } | null
  onChange: (selection: EntityPickerSelection) => void
  /**
   * TEST ONLY: pre-seed the internal query state so create-items surface in
   * unit tests without simulating keyboard events in the Combobox input.
   */
  _testQuery?: string
}

// ---------------------------------------------------------------------------
// ID encoding helpers
// ---------------------------------------------------------------------------

const MERCHANT_PREFIX = "merchant:"
const PERSON_PREFIX = "person:"
const CREATE_MERCHANT_PREFIX = "create-merchant:"
const CREATE_PERSON_PREFIX = "create-person:"

function merchantId(id: string) {
  return `${MERCHANT_PREFIX}${id}`
}
function personId(id: string) {
  return `${PERSON_PREFIX}${id}`
}

// ---------------------------------------------------------------------------
// buildCreateItems (exported for unit tests)
// ---------------------------------------------------------------------------

export interface CreateItem {
  id: string
  kind: "create-merchant" | "create-person"
  label: string
  name: string
}

export function buildCreateItems(query: string): CreateItem[] {
  const trimmed = query.trim()
  if (!trimmed) return []

  const predicted = classifyEntity({ paymentMethod: null, extractedName: trimmed })

  const merchantItem: CreateItem = {
    id: `${CREATE_MERCHANT_PREFIX}${trimmed}`,
    kind: "create-merchant",
    label: `Create merchant "${trimmed}"`,
    name: trimmed,
  }
  const personItem: CreateItem = {
    id: `${CREATE_PERSON_PREFIX}${trimmed}`,
    kind: "create-person",
    label: `Create person "${trimmed}"`,
    name: trimmed,
  }

  // Predicted type comes first; "unknown" → merchant first (convention).
  return predicted === "person" ? [personItem, merchantItem] : [merchantItem, personItem]
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EntityPicker({
  merchants,
  persons,
  value,
  onChange,
  _testQuery,
}: EntityPickerProps) {
  const [query, setQuery] = useState(_testQuery ?? "")

  // Detect whether the typed query has an exact match among existing entities.
  const lowerQuery = query.trim().toLowerCase()
  const hasExactMatch =
    lowerQuery.length > 0 &&
    (merchants.some((m) => m.cleanName.toLowerCase() === lowerQuery) ||
      persons.some((p) => p.name.toLowerCase() === lowerQuery))

  const createItems = hasExactMatch ? [] : buildCreateItems(query)

  // Build the flat item ID list: all merchants, then all persons, then create options.
  const allItems: string[] = [
    ...merchants.map((m) => merchantId(m.id)),
    ...persons.map((p) => personId(p.id)),
    ...createItems.map((c) => c.id),
  ]

  // Derive display label for a given item ID.
  function labelFor(id: string): string {
    if (id.startsWith(MERCHANT_PREFIX)) {
      const mid = id.slice(MERCHANT_PREFIX.length)
      return merchants.find((m) => m.id === mid)?.cleanName ?? mid
    }
    if (id.startsWith(PERSON_PREFIX)) {
      const pid = id.slice(PERSON_PREFIX.length)
      return persons.find((p) => p.id === pid)?.name ?? pid
    }
    if (id.startsWith(CREATE_MERCHANT_PREFIX) || id.startsWith(CREATE_PERSON_PREFIX)) {
      const item = createItems.find((c) => c.id === id)
      return item?.label ?? id
    }
    return id
  }

  // Derive current value ID.
  const currentValueId = value
    ? value.type === "merchant"
      ? merchantId(value.id)
      : personId(value.id)
    : null

  function handleValueChange(id: string | null) {
    if (!id) {
      onChange({ kind: "clear" })
      return
    }

    if (id.startsWith(MERCHANT_PREFIX)) {
      onChange({
        kind: "existing",
        entity: { type: "merchant", id: id.slice(MERCHANT_PREFIX.length) },
      })
      return
    }
    if (id.startsWith(PERSON_PREFIX)) {
      onChange({ kind: "existing", entity: { type: "person", id: id.slice(PERSON_PREFIX.length) } })
      return
    }
    if (id.startsWith(CREATE_MERCHANT_PREFIX)) {
      const name = id.slice(CREATE_MERCHANT_PREFIX.length)
      onChange({ kind: "create", type: "merchant", name })
      return
    }
    if (id.startsWith(CREATE_PERSON_PREFIX)) {
      const name = id.slice(CREATE_PERSON_PREFIX.length)
      onChange({ kind: "create", type: "person", name })
    }
  }

  return (
    <div className="space-y-1.5">
      <Label className="text-[11px] text-text-muted">Linked to</Label>
      <Combobox
        items={allItems}
        itemToStringLabel={labelFor}
        value={currentValueId}
        onValueChange={handleValueChange}
      >
        <ComboboxInput
          placeholder="Search merchants & people…"
          showClear={!!currentValueId}
          className="h-7"
          value={query}
          onChange={(e) => setQuery((e.target as HTMLInputElement).value)}
        />
        <ComboboxContent>
          <ComboboxEmpty>No matches — type a name to create one</ComboboxEmpty>
          <ComboboxList>
            {(id: string) => {
              if (id.startsWith(CREATE_MERCHANT_PREFIX) || id.startsWith(CREATE_PERSON_PREFIX)) {
                return (
                  <ComboboxItem key={id} value={id} className="text-text-muted italic">
                    {labelFor(id)}
                  </ComboboxItem>
                )
              }
              return (
                <ComboboxItem key={id} value={id}>
                  {labelFor(id)}
                </ComboboxItem>
              )
            }}
          </ComboboxList>
        </ComboboxContent>
      </Combobox>
    </div>
  )
}
